import type { OperatingCalendar } from "../api-contracts/operating-calendar.js";

// ── FONTE ÚNICA da resolução "regras do calendário → janelas concretas" ──────────────
// Consumida por: rodada detalhada (enqueue), preview server, aviso de OP, gate do sweep. O client
// NUNCA resolve regras — recebe o resultado (research). Timezone via Intl nativo (research):
// offset calculado POR INSTANTE (duas passadas), então DST é tratado mesmo que o Brasil não o use.

export interface ResolvedWindows {
  /** Janelas UTC [startMs, endMs) recortadas ao período, ordenadas e com adjacências fundidas. */
  windows: [number, number][];
  effectiveMs: number;
  effectiveHours: number;
}

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = formatterCache.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatterCache.set(timeZone, fmt);
  }
  return fmt;
}

function wallClockParts(utcMs: number, timeZone: string) {
  const parts: Record<string, number> = {};
  for (const p of getFormatter(timeZone).formatToParts(new Date(utcMs))) {
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  }
  return parts as {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
  };
}

/** Offset (ms) da timezone no instante UTC dado — positivo a leste de Greenwich. */
function tzOffsetMs(utcMs: number, timeZone: string): number {
  const w = wallClockParts(utcMs, timeZone);
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - utcMs;
}

/**
 * Converte hora de parede local (data civil + minuto do dia, pode exceder 1440) em UTC ms.
 *
 * Spec 082 (T013) — deixou de ser privada. A importação de histórico recebe DIAS CIVIS numa coluna
 * de planilha e precisa transformá-los em instante para achar a janela do dia e gravar `startedAt`.
 * Esta é a única conversão correta disso no repositório, e o motivo de ela não poder ser
 * reimplementada na feature: `new Date("2026-02-02")` chega como meia-noite UTC, não meia-noite de
 * Fortaleza, e o histórico inteiro entra deslocado em um dia sem nada acusar. O algoritmo é de duas
 * passadas porque o offset depende do instante, então DST é tratado mesmo onde não existe hoje.
 */
export function wallTimeToUtc(
  year: number,
  month: number,
  day: number,
  minute: number,
  timeZone: string
): number {
  const guess = Date.UTC(year, month - 1, day, 0, minute);
  const once = guess - tzOffsetMs(guess, timeZone);
  return guess - tzOffsetMs(once, timeZone);
}

function mergeWindows(raw: [number, number][]): [number, number][] {
  const sorted = raw.filter(([s, e]) => e > s).sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const w of sorted) {
    const last = merged[merged.length - 1];
    if (last && w[0] <= last[1]) last[1] = Math.max(last[1], w[1]);
    else merged.push([w[0], w[1]]);
  }
  return merged;
}

export function resolveWindows(
  calendar: OperatingCalendar | null | undefined,
  periodStartUtc: Date,
  periodEndUtc: Date,
  timezone: string
): ResolvedWindows {
  const startMs = periodStartUtc.getTime();
  const endMs = periodEndUtc.getTime();
  if (endMs <= startMs) return { windows: [], effectiveMs: 0, effectiveHours: 0 };
  if (!calendar) {
    return {
      windows: [[startMs, endMs]],
      effectiveMs: endMs - startMs,
      effectiveHours: (endMs - startMs) / HOUR_MS,
    };
  }

  const exceptionByDate = new Map(calendar.exceptions.map((e) => [e.date, e]));
  const raw: [number, number][] = [];

  // Enumera datas civis locais de (início − 2 dias) até o fim do período: um turno declarado no
  // dia D pode atravessar a meia-noite (endMinute ≤ 2879) e invadir o período que começa em D+1.
  const first = wallClockParts(startMs, timezone);
  const last = wallClockParts(endMs, timezone);
  let cursor = Date.UTC(first.year, first.month - 1, first.day) - 2 * DAY_MS;
  const lastDay = Date.UTC(last.year, last.month - 1, last.day);
  for (; cursor <= lastDay; cursor += DAY_MS) {
    const d = new Date(cursor);
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    const weekday = d.getUTCDay();
    const dateStr = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const exception = exceptionByDate.get(dateStr);

    // CLOSED derruba TODAS as janelas que INICIAM no dia (o overflow de um turno noturno da
    // véspera permanece — o turno pertence ao dia em que começa).
    if (exception?.kind !== "CLOSED") {
      for (const shift of calendar.weeklyShifts) {
        if (shift.weekday !== weekday) continue;
        raw.push([
          wallTimeToUtc(year, month, day, shift.startMinute, timezone),
          wallTimeToUtc(year, month, day, shift.endMinute, timezone),
        ]);
      }
    }
    if (exception?.kind === "EXTRA_WINDOW") {
      raw.push([
        wallTimeToUtc(year, month, day, exception.startMinute, timezone),
        wallTimeToUtc(year, month, day, exception.endMinute, timezone),
      ]);
    }
  }

  const clipped = raw
    .map(([s, e]): [number, number] => [Math.max(s, startMs), Math.min(e, endMs)])
    .filter(([s, e]) => e > s);
  const windows = mergeWindows(clipped);
  const effectiveMs = windows.reduce((acc, [s, e]) => acc + (e - s), 0);
  return { windows, effectiveMs, effectiveHours: effectiveMs / HOUR_MS };
}

/**
 * Spec 082 (T014) — quantos DIAS CIVIS distintos têm expediente, dentro de janelas já resolvidas.
 *
 * É o divisor da meta derivada: a meta do período dividida pelos dias úteis do calendário. Conta a
 * partir de `resolveWindows` de propósito, para que exista uma implementação só da regra de
 * calendário — o teste de paridade SQL↔TS já cobre a resolução, e uma função SQL de dias úteis
 * abriria uma segunda execução da mesma regra para divergir da primeira.
 *
 * Um turno que atravessa a meia-noite conta UM dia, o dia em que ele começa. É a mesma convenção
 * que `resolveWindows` já usa para decidir de quem é o turno noturno, e trocá-la aqui faria a
 * mesma semana ter dois números de dias úteis dependendo de quem pergunta.
 *
 * ⚠️ Sem calendário nenhum, `resolveWindows` devolve UMA janela cobrindo o período inteiro, e esta
 * função devolveria `1` para um mês. Quem chama precisa distinguir "não há calendário" de "há
 * calendário e ele tem um dia útil" ANTES de dividir — a ausência de calendário significa que não
 * existe divisor, não que ele vale um.
 */
export function countBusinessDays(windows: [number, number][], timeZone: string): number {
  const days = new Set<string>();
  for (const [startMs] of windows) {
    const w = wallClockParts(startMs, timeZone);
    days.add(
      `${String(w.year).padStart(4, "0")}-${String(w.month).padStart(2, "0")}-${String(w.day).padStart(2, "0")}`
    );
  }
  return days.size;
}

/** Horizonte de busca do sweep: exceções são datadas e turnos são semanais — 60 dias cobre. */
const NEXT_OPENING_SEARCH_MS = 60 * DAY_MS;

/**
 * Próximo instante ≥ fromUtc em que a linha está aberta. Retorna o próprio fromUtc se já estiver
 * dentro de janela; null se nenhuma janela abrir no horizonte de busca (ex.: calendário vazio).
 */
export function nextWindowOpening(
  calendar: OperatingCalendar | null | undefined,
  fromUtc: Date,
  timezone: string
): Date | null {
  if (!calendar) return fromUtc;
  const { windows } = resolveWindows(
    calendar,
    fromUtc,
    new Date(fromUtc.getTime() + NEXT_OPENING_SEARCH_MS),
    timezone
  );
  const firstWindow = windows[0];
  return firstWindow ? new Date(firstWindow[0]) : null;
}

/** true se o instante cai dentro de uma janela operacional (semântica meio-aberta [início, fim)). */
export function isWithinWindow(
  calendar: OperatingCalendar | null | undefined,
  atUtc: Date,
  timezone: string
): boolean {
  if (!calendar) return true;
  const { effectiveMs } = resolveWindows(calendar, atUtc, new Date(atUtc.getTime() + 1), timezone);
  return effectiveMs > 0;
}
